import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function serverPublic() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

export type PublicTestimonial = {
  id: string;
  author: string;
  role: string;
  content: string;
  rating: number;
  source: string;
};

export type PublicTeamMember = {
  id: string;
  name: string;
  role: string;
  bio: string;
  photo_url: string | null;
  linkedin_url: string | null;
};

export type PublicPost = {
  id: string;
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  category: string;
  cover: string;
  body: string;
  author: string;
  reading_time: string;
  published_at: string | null;
  cover_image: string | null;
  youtube_urls: string[];
};

// Lightweight shape for list views (cards). Excludes heavy `body` and `youtube_urls`
// so we don't ship post/location content over the wire on index/home pages.
export type PublicPostCard = Omit<PublicPost, "body" | "youtube_urls">;
export type PublicServiceLocationCard = Omit<PublicServiceLocation, "body">;

export const listTestimonials = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("testimonials")
    .select("id, author, role, content, rating, source")
    .eq("featured", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicTestimonial[];
  return (data ?? []) as PublicTestimonial[];
});

export const listTeam = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("team_members")
    .select("id, name, role, bio, photo_url, linkedin_url")
    .eq("active", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicTeamMember[];
  return (data ?? []) as PublicTeamMember[];
});

export const listPublishedPosts = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("posts")
    .select("id, slug, title, description, excerpt, category, cover, author, reading_time, published_at, cover_image")
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(5000);
  if (error) return [] as PublicPostCard[];
  return (data ?? []) as unknown as PublicPostCard[];
});

// Slim: only the last N posts, used on home page.
export const listLatestPosts = createServerFn({ method: "GET" })
  .inputValidator((d: { limit?: number }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const limit = Math.min(Math.max(data.limit ?? 6, 1), 24);
    const { data: rows, error } = await sb
      .from("posts")
      .select("id, slug, title, description, excerpt, category, cover, reading_time, published_at, cover_image")
      .eq("published", true)
      .order("published_at", { ascending: false, nullsFirst: false })
      .limit(limit);
    if (error) return [] as Array<Omit<PublicPostCard, "author">>;
    return (rows ?? []) as unknown as Array<Omit<PublicPostCard, "author">>;
  });

// Slim searchable lists — used only by SiteSearch, minimum columns.
export type SearchPost = { slug: string; title: string; excerpt: string; category: string };
export type SearchLocation = { slug: string; title: string; city: string; service_label: string; description: string };

export const listPostsSearch = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("posts")
    .select("slug, title, excerpt, description, category")
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(5000);
  if (error) return [] as SearchPost[];
  return (data ?? []).map((p: any) => ({
    slug: p.slug, title: p.title,
    excerpt: p.excerpt || p.description || "",
    category: p.category,
  })) as SearchPost[];
});

export const listLocationsSearch = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("service_locations" as any)
    .select("slug, title, city, service_label, description")
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(1000);
  if (error) return [] as SearchLocation[];
  return (data ?? []) as unknown as SearchLocation[];
});

// Slim locations for footer/menu — only fields displayed.
export type MenuLocation = { id: string; slug: string; city: string; neighborhood: string | null; service_label: string; title: string };

export const listLocationsMenu = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("service_locations" as any)
    .select("id, slug, city, neighborhood, service_label, title")
    .eq("published", true)
    .order("city", { ascending: true })
    .limit(1000);
  if (error) return [] as MenuLocation[];
  return (data ?? []) as unknown as MenuLocation[];
});

export const getPublishedPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: post } = await sb
      .from("posts")
      .select("id, slug, title, description, excerpt, category, cover, body, author, reading_time, published_at, cover_image, youtube_urls")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    return (post ?? null) as PublicPost | null;
  });

// Returns the post plus the active internal-link rules and pre-rendered HTML
// (markdown body converted to HTML and internal links auto-applied).
export const getRenderedPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const [postRes, linksRes] = await Promise.all([
      sb
        .from("posts")
        .select("id, slug, title, description, excerpt, category, cover, body, author, reading_time, published_at, cover_image, youtube_urls")
        .eq("slug", data.slug)
        .eq("published", true)
        .maybeSingle(),
      sb
        .from("internal_links")
        .select("anchor, url, priority")
        .eq("active", true)
        .order("priority", { ascending: false }),
    ]);
    const post = postRes.data;
    if (!post) return null;
    const { marked } = await import("marked");
    const { applyInternalLinks, transformFaqAccordions, addHeadingIds } = await import("./internal-links");
    const { sanitizePostHtml, ensureImageAlts } = await import("./sanitize-html.server");
    const rawHtml = await marked.parse(post.body || "");
    const safeHtml = ensureImageAlts(sanitizePostHtml(rawHtml as string), post.title);
    const linked = applyInternalLinks(safeHtml, (linksRes.data ?? []) as any);
    const html = addHeadingIds(transformFaqAccordions(linked));
    return { ...post, html } as PublicPost & { html: string };
  });

// ───────── Videos ─────────

export type PublicVideo = {
  id: string;
  title: string;
  url: string;
  description: string;
  thumbnail_url: string | null;
};

export const listPublishedVideos = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("blog_videos" as any)
    .select("id, title, url, description, thumbnail_url")
    .eq("published", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicVideo[];
  return (data ?? []) as unknown as PublicVideo[];
});

// ───────── Service Locations ─────────

export type PublicServiceLocation = {
  id: string;
  slug: string;
  service_slug: string;
  service_label: string;
  city: string;
  neighborhood: string | null;
  title: string;
  description: string;
  body: string;
  cover_image: string | null;
  published_at: string | null;
};

export const listPublishedLocations = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("service_locations" as any)
    .select("id, slug, service_slug, service_label, city, neighborhood, title, description, cover_image, published_at")
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(1000);
  if (error) return [] as PublicServiceLocationCard[];
  return (data ?? []) as unknown as PublicServiceLocationCard[];
});

// ───────── Related posts ─────────

export type RelatedPost = {
  slug: string; title: string; category: string;
  cover: string; cover_image: string | null;
  excerpt: string; reading_time: string;
};

export const listRelatedPosts = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; category: string; limit?: number }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const limit = data.limit ?? 4;
    // Prefer same-category posts
    const sameCat = await sb
      .from("posts")
      .select("slug, title, category, cover, cover_image, excerpt, description, reading_time")
      .eq("published", true)
      .eq("category", data.category)
      .neq("slug", data.slug)
      .order("published_at", { ascending: false, nullsFirst: false })
      .limit(limit);
    const rows = sameCat.data ?? [];
    if (rows.length < limit) {
      const fill = await sb
        .from("posts")
        .select("slug, title, category, cover, cover_image, excerpt, description, reading_time")
        .eq("published", true)
        .neq("slug", data.slug)
        .neq("category", data.category)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(limit - rows.length);
      for (const r of fill.data ?? []) rows.push(r as any);
    }
    return rows.map((p: any) => ({
      slug: p.slug, title: p.title, category: p.category,
      cover: p.cover, cover_image: p.cover_image,
      excerpt: p.excerpt || p.description || "",
      reading_time: p.reading_time,
    })) as RelatedPost[];
  });

// ───────── Services (public) ─────────

export type PublicService = {
  id: string; slug: string; label: string;
  description: string; icon: string; route_path: string;
  order_index: number;
};

export const listActiveServices = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("services" as any)
    .select("id, slug, label, description, icon, route_path, order_index")
    .eq("active", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicService[];
  return (data ?? []) as unknown as PublicService[];
});

export const getRenderedLocation = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const [locRes, linksRes] = await Promise.all([
      sb
        .from("service_locations" as any)
        .select("id, slug, service_slug, service_label, city, neighborhood, title, description, body, cover_image, published_at")
        .eq("slug", data.slug)
        .eq("published", true)
        .maybeSingle(),
      sb
        .from("internal_links")
        .select("anchor, url, priority")
        .eq("active", true)
        .order("priority", { ascending: false }),
    ]);
    const loc = locRes.data as unknown as PublicServiceLocation | null;
    if (!loc) return null;
    const { marked } = await import("marked");
    const { applyInternalLinks, transformFaqAccordions, addHeadingIds } = await import("./internal-links");
    const { sanitizePostHtml, ensureImageAlts } = await import("./sanitize-html.server");
    const rawHtml = await marked.parse(loc.body || "");
    const safeHtml = ensureImageAlts(sanitizePostHtml(rawHtml as string), loc.title);
    const linked = applyInternalLinks(safeHtml, (linksRes.data ?? []) as any);
    const html = addHeadingIds(transformFaqAccordions(linked));
    return { ...loc, html } as PublicServiceLocation & { html: string };
  });